import { Injectable } from '@angular/core';
import { NgbDateParserFormatter, NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { ResourcesService } from '../../resources/resources.service';

function padNumber(value: number) {
  return isNumber(value) ? `0${value}`.slice(-2) : "";
}

function isNumber(value: any): boolean {
  return !isNaN(toInteger(value));
}

function toInteger(value: any): number {
  return parseInt(`${value}`, 10);
}

@Injectable({
  providedIn: 'root'
})
export class NgbDateGlobalParserFormatter extends NgbDateParserFormatter {

  private globalDateFormat: string;
  private globalTimeFormat: string;
  private useMeridian: boolean;
  private globalDateSep;
  private globalTimeSep: string;

  constructor(private resources: ResourcesService) {
    super();
    this.globalDateFormat = localStorage.getItem('globalDateFormat');
    this.globalTimeFormat = localStorage.getItem('globalTimeFormat');
    this.useMeridian = localStorage.getItem('globalTime12H') === 'true';
    this.globalDateSep = localStorage.getItem('globalDateSep');
    this.globalTimeSep = localStorage.getItem('globalTimeSep');
  }

  get UseMeridian(): boolean {
    return this.useMeridian;
  }

  get GlobalDateFormat(): string {
    return this.globalDateFormat;
  }

  get GlobalDateSep(): string {
    return this.globalDateSep;
  }

  get DatePattern(): RegExp {
    return this.resources.dateFormats[this.globalDateFormat];
  }

  get DateRangePattern(): RegExp {
    return this.resources.dateFormats.range[this.globalDateFormat];
  }

  get DateFormatRequest(): string {
    return this.globalDateFormat.replace("DD", "dd").replace("YYYY", "yyyy");
  }

  get TimeFormatRequest(): string {
    return this.useMeridian ? this.globalTimeFormat.toLowerCase() : this.globalTimeFormat.replace("MM", "mm").replace("SS", "ss");
  }

  get DateTimeFormatRequest(): string {
    // Mobilize-Note: osalazar. Remove AM/PM string from the time format.
    return `${this.DateFormatRequest} ${this.TimeFormatRequest.split(" ")[0]} ${this.useMeridian ? 'tt' : ''}`;
  }

  getDatePropertyIndex(format: string, formatIndexes: any) {
    return formatIndexes[0] === format
      ? 0
      : formatIndexes[1] === format
        ? 1
        : 2
  }

  getDatePropertyValue(formatIndex: number, formatIndexes: any, date: NgbDateStruct) {
    let dateProperty = this.getDateProperty(formatIndexes[formatIndex])
    return dateProperty === 'year'
      ? date[dateProperty]
      : isNumber(date[dateProperty])
        ? padNumber(date[dateProperty])
        : '';
  }

  getDateProperty(format: string) {
    return format === 'DD'
      ? 'day'
      : format === 'MM'
        ? 'month'
        : 'year'
  }

  parse(value: string): NgbDateStruct {
    if (value) {
      const dateParts = value.trim().split(this.globalDateSep);
      const formatIndexes: string[] = this.globalDateFormat.split(this.globalDateSep);
      if (dateParts.length === 3 && isNumber(dateParts[0]) && isNumber(dateParts[1]) && isNumber(dateParts[2])) {
        return {
          day: toInteger(dateParts[this.getDatePropertyIndex("DD", formatIndexes)]),
          month: toInteger(dateParts[this.getDatePropertyIndex("MM", formatIndexes)]),
          year: toInteger(dateParts[this.getDatePropertyIndex("YYYY", formatIndexes)])
        };
      }
    }
    return null;
  }

  format(date: NgbDateStruct): string {
    const formatIndexes: string[] = this.globalDateFormat.split(this.globalDateSep);
    return date
      ? `${this.getDatePropertyValue(0, formatIndexes, date)}${this.globalDateSep}${this.getDatePropertyValue(1, formatIndexes, date)}${this.globalDateSep}${this.getDatePropertyValue(2, formatIndexes, date)}`
      : '';
  }

  getRangeFromString(rangeString: string) {
    let range = rangeString.replace(/\s/g, '');
    let fromDate = this.ConvertUserFormatToSqlString(this.parse(range.substring(0, this.globalDateFormat.length)));
    let toDate = undefined;

    if (range.length > this.globalDateFormat.length) {
      toDate = this.ConvertUserFormatToSqlString(this.parse(range.substring(this.globalDateFormat.length + 1)));
    }

    return { fromDate: fromDate, toDate: toDate }
  }

  getRangeFromDate(fromDate: NgbDateStruct) {
    return { fromDate: this.ConvertUserFormatToSqlString(fromDate), toDate: undefined }
  }

  ConvertUserFormatToSqlString(date: NgbDateStruct): string {
    return date
      ? `${date.year}-${isNumber(date.month) ? padNumber(date.month) : ''}-${isNumber(date.day) ? padNumber(date.day) : ''}`
      : '';
  }

  getFormattedTime(time: NgbTimeStruct): string {
    return time ? `${padNumber(time.hour)}${this.globalTimeSep}${padNumber(time.minute)}${this.globalTimeSep}${padNumber(time.second)}` : ''
  }
}
